import React, { useState } from 'react';
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded';
import { useAuth } from '../../../context/AuthContext';
import { accountService } from '../../../services/api/accountService';
import NkySnackbarAlert from '../NkySnackbarAlert/NkySnackbarAlert';
import NkySpinner from '../NkySpinner/NkySpinner';
import {
  StyledVerificationDialog,
  VerificationModalTitle,
  VerificationModalContent,
  VerificationLeadText,
  VerificationEmailHighlight,
  VerificationModalActions,
  SecondaryModalButton,
  PrimaryModalButton,
  VerificationErrorAlert,
} from './EmailVerificationRequiredModal.styled';

/**
 * Blocks the app until the signed-in user verifies their email.
 * Shown when `user.ValidEmail === false`.
 */
function EmailVerificationRequiredModal({ open }) {
  const { user, refreshUser } = useAuth();
  const [resendLoading, setResendLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const handleResend = async () => {
    setInlineError('');
    setResendLoading(true);
    try {
      const status = await accountService.resendVerificationEmail();
      if (status === 201) {
        setToast({
          open: true,
          severity: 'success',
          message: 'Verification email sent. Check your inbox and spam folder.',
        });
      } else if (status === 409) {
        await refreshUser();
      } else {
        setInlineError('Could not send the verification email. Please try again.');
      }
    } catch (err) {
      if (err?.status === 409) {
        await refreshUser();
        return;
      }
      setInlineError('Could not send the verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleRefresh = async () => {
    setInlineError('');
    setRefreshLoading(true);
    try {
      const result = await refreshUser();
      if (!result.success) {
        setInlineError('Could not refresh your account. Try signing in again.');
        return;
      }
      if (result.userData?.ValidEmail) {
        setToast({
          open: true,
          severity: 'success',
          message: 'Your email is verified. Welcome!',
        });
      } else {
        setToast({
          open: true,
          severity: 'info',
          message: 'Your email is not verified yet. Check your inbox for the link.',
        });
      }
    } catch {
      setInlineError('Could not refresh your account. Please try again.');
    } finally {
      setRefreshLoading(false);
    }
  };

  const busy = resendLoading || refreshLoading;

  return (
    <>
      <StyledVerificationDialog
        open={open}
        disableEscapeKeyDown
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
        }}
      >
        <VerificationModalTitle>
          <span className="verification-modal-icon-wrap" aria-hidden>
            <MarkEmailUnreadRoundedIcon className="verification-modal-icon" />
          </span>
          Verify your email
        </VerificationModalTitle>

        <VerificationModalContent>
          {inlineError ? (
            <VerificationErrorAlert severity="error">{inlineError}</VerificationErrorAlert>
          ) : null}
          <VerificationLeadText>
            You need to verify your email before using NKY Connect. We sent a link to{' '}
            <VerificationEmailHighlight>{user?.Email}</VerificationEmailHighlight>.
            Open that link, then tap Refresh below.
          </VerificationLeadText>
          <VerificationLeadText>
            Didn&apos;t get it? Resend the verification email.
          </VerificationLeadText>
        </VerificationModalContent>

        <VerificationModalActions>
          <SecondaryModalButton
            variant="outlined"
            onClick={handleRefresh}
            disabled={busy}
            startIcon={refreshLoading ? <NkySpinner size="sm" onDark /> : null}
          >
            {refreshLoading ? 'Refreshing…' : 'Refresh'}
          </SecondaryModalButton>
          <PrimaryModalButton
            variant="contained"
            onClick={handleResend}
            disabled={busy}
            startIcon={resendLoading ? <NkySpinner size="sm" onDark /> : null}
          >
            {resendLoading ? 'Sending…' : 'Resend verification email'}
          </PrimaryModalButton>
        </VerificationModalActions>
      </StyledVerificationDialog>

      <NkySnackbarAlert
        open={toast.open}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
}

export default EmailVerificationRequiredModal;
